import { Kinesis } from '@/services/TrackerService';
import { ILogFields } from '@/services/TrackerService/Kinesis/interfaces';
import ImpressionStore from '@/stores/ImpressionStore';
import Observer, { Props } from '@researchgate/react-intersection-observer';
import { prop } from 'ramda';
import React from 'react';

export interface IImpressionProps<T> {
  children: React.ReactNode;
  /** 중복 호출을 막기위한 ID */
  id?: string | number;
  /** log data */
  logData?: T;
  /**
   * intersection-observer 옵션
   * @see https://github.com/researchgate/react-intersection-observer
   */
  observerOptions?: Omit<Props, 'children' | 'onChange'>;
  /**
   * impression log 전송 함수
   * 없는 경우 Kinesis.impression 호출됨
   */
  sendLog?: (data: T) => Promise<void>;
}

const impressionStore = new ImpressionStore();
export const impressionLogHistory = impressionStore.logHistory;
export const impressionBeforeUnload = impressionStore.onBeforeUnload;

/**
 * Impression 클라이언트 로그(Kinesis) 로그를 전송한다.
 *
 * @example
 * <Impression {...}>
 *    <SunnyLink href="/">link</SunnyLink>
 * </Impression>
 */
const Impression = <T extends Partial<ILogFields>>({
  children,
  id,
  logData,
  observerOptions,
  sendLog,
}: IImpressionProps<T>): React.ReactElement => {
  const isValidElement = React.isValidElement(children) && children.type !== React.Fragment;
  const handleChange = (entry: IntersectionObserverEntry, unobserve: () => void): void => {
    if (!logData || !entry.isIntersecting) {
      return;
    }

    if (id && impressionStore.isLogged(id)) {
      unobserve();
      return;
    }

    if (sendLog) {
      sendLog(logData);
    } else {
      const pageName = prop('pageName', logData) ?? '';
      Kinesis.impression(pageName, logData);
    }

    if (id) {
      impressionStore.recordLog(id, logData);
    }

    unobserve();
  };

  return (
    <Observer onChange={handleChange} {...observerOptions}>
      {!isValidElement ? <div>{children}</div> : children}
    </Observer>
  );
};

export default Impression;
